import { faEye, faEyeSlash, faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertTitle, Box, Button, Checkbox, CircularProgress, IconButton, InputAdornment, MenuItem, Table, TableBody, TableCell, TableRow, TextField, TextFieldProps, Typography, styled, useTheme } from '@mui/material';
import { grey } from "@mui/material/colors";
import { Dispatch, FC, HTMLInputTypeAttribute, ReactNode, SetStateAction, useCallback, useContext, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ContentVersion, UserRegistration } from "../api/dtos/dtos";
import Center from "../components/common/Center";
import Hr from "../components/common/HorizontalRule";
import { UserFormatError } from "../constants/exceptions/CustomExceptions";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "../constants/styles/AlertConstants";
import AuthContext from "../contexts/auth/AuthContext";
import { SpeciesContext } from "../contexts/data/SpeciesContext";
import AlertContext from "../contexts/styles/AlertContext";
import useServiceStackClient from "../hooks/api/useServiceStackClient";
import useGetTermsOfUseContent from "../hooks/data/useGetTermsOfUseContent";
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

interface IStyledTableRowProps {
    primaryLabel: string;
    secondaryLabel?: string;

    type?: HTMLInputTypeAttribute;

    fieldState?: FieldState;

    select?: boolean;
    items?: ReactNode[];

    disabled?: boolean;

    value: any;
    onChange?: Dispatch<SetStateAction<any>>
}

type FieldState = {
    state?: "error" | "warning";
    message?: string;
}

const StyledTableCell = styled(TableCell)(() => ({
    padding: '8px',
    border: 'none'
}));

const StyledTextField = styled(TextField, { shouldForwardProp: (props) => props !== 'stateColor' })<TextFieldProps & { stateColor?: string }>(({ theme, stateColor, disabled }) => ({
    '& label': {
        color: stateColor === 'error' ?
            theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700],
    },
    '& p': {
        color: stateColor === 'error' ?
            theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700]
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: stateColor === 'error' ?
                `${theme.palette.error.main}77` :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    'rgba(0, 0, 0, 0.2)',
        },
        '&:hover fieldset': {
            borderColor: stateColor === 'error' ?
                theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    disabled ?
                        'rgba(0, 0, 0, 0.2)' :
                        'rgb(0, 0, 0)',
        },
        '&.Mui-focused fieldset': {
            borderColor: stateColor === 'error' ?
                theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    theme.palette.primary.main
        },
    },
}));

const RegisterContainer = styled(Box)(() => ({
    padding: '30px 14px 8px 14px'
}));

const StyledConfirmButton = styled(Button)(({ theme }) => ({
    maxWidth: '100px',
    minWidth: '100px',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: theme.palette.primary.main,
    color: grey[100],
    marginLeft: '30px',

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const StyledTermsOfUseParentContainer = styled(Box)(() => ({
    marginTop: '10px',
    padding: '30px 14px 30px 14px'
}));

const StyledTermsOfUseContainer = styled(Box)(() => ({
    padding: '30px 14px 30px 14px',
    height: '300px',
    overflowY: 'scroll',
    borderTop: 'solid',
    borderBottom: 'solid',
    borderColor: 'grey'
}));

const StyledTableRow: FC<IStyledTableRowProps> = ({
    primaryLabel,
    secondaryLabel,
    type,
    select,
    items,
    fieldState,
    disabled,
    value,
    onChange
}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    return (
        <TableRow>
            <StyledTableCell sx={{ minWidth: '124px', maxWidth: '124px' }}>
                {primaryLabel}:
            </StyledTableCell>
            <StyledTableCell width='100%'>
                {
                    select ?
                        <StyledTextField
                            select
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        >
                            {items}
                        </StyledTextField> :
                        <StyledTextField
                            id={`Text-Field-${primaryLabel.replaceAll(' ', '-')}`}
                            inputProps={{
                                autoComplete: 'password',
                            }}
                            InputProps={{
                                endAdornment: type === 'password' ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            disableRipple
                                            sx={{ width: '24px', heigh: '24px' }}
                                            onClick={() => setShowPassword(prev => !prev)}
                                        >
                                            {
                                                showPassword ?
                                                    <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.2rem' }} /> :
                                                    <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.2rem' }} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                ) :
                                    disabled ? (
                                        <InputAdornment position="end">
                                            <FontAwesomeIcon icon={faLock} style={{ fontSize: '1.2rem' }} />
                                        </InputAdornment>
                                    ) :
                                        null
                            }}
                            autoComplete='password'
                            type={type === 'password' ? !showPassword ? 'password' : 'text' : type}
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                }
            </StyledTableCell>
        </TableRow>
    )
}

const RegisterContent: FC = () => {

    const theme = useTheme();
    const client = useServiceStackClient();
    const navigate = useNavigate()

    // -- Context --
    const { refresh } = useContext(AuthContext);
    const { addAlert } = useContext(AlertContext);
    const { species: { species } } = useContext(SpeciesContext);
    const { species: speciesName } = useParams();

    // -- Registration state --
    const [userRegistrationDetails, setUserRegistrationDetails] = useState<UserRegistration | null>(null);
    const [userRegistrationDetailsState, setUserRegistrationDetailsState] = useState<Record<string, FieldState>>({});

    // -- Password state --
    const [password, setPassword] = useState<{ password: string, confirmPassword: string }>({
        password: '',
        confirmPassword: ''
    });
    const [passwordState, setPasswordState] = useState<Record<string, FieldState>>({});

    // -- Terms of use state --
    const [termsOfUseState, setTermsOfUseState] = useState<{ agree: boolean, contentVersion: ContentVersion | undefined }>({
        agree: false,
        contentVersion: undefined
    });

    //
    // -- Queries --
    const { data: termsOfUseContent, loading: termsOfUseContentLoading } = useGetTermsOfUseContent(
        { species: species, contentName: speciesName?.replace("scan", "")?.replace("alert", "") + "_termsofuse" }
    );

    useMemo(() => {
        setTermsOfUseState({ agree: termsOfUseState.agree, contentVersion: termsOfUseContent });
    }, [termsOfUseContent]);

    const states = useMemo(() => {
        return [
            <MenuItem key={"act"} value="ACT">ACT</MenuItem>,
            <MenuItem key={"nsw"} value="NSW">NSW</MenuItem>,
            <MenuItem key={"nt"} value="NT">NT</MenuItem>,
            <MenuItem key={"qld"} value="QLD">QLD</MenuItem>,
            <MenuItem key={"sa"} value="SA">SA</MenuItem>,
            <MenuItem key={"tas"} value="TAS">TAS</MenuItem>,
            <MenuItem key={"vic"} value="VIC">VIC</MenuItem>,
            <MenuItem key={"wa"} value="WA">WA</MenuItem>,
            <MenuItem key={"other"} value="OTHER">OTHER</MenuItem>
        ];
    }, []);

    // Validate the user profile fields
    const validateUserRegistrationField = useCallback((field: string, value: any, warn?: boolean): boolean => {
        // Confirm the field is a key of user registration type
        if (field as keyof UserRegistration) {
            // Update user registration full name
            if (field === 'fullName') {
                const valid = value && value !== ''

                if (!valid) {
                    setUserRegistrationDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Name is required.'
                            }
                        };
                    });
                }
                else {
                    setUserRegistrationDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else if (field === 'userName') { // Update user registration username
                const valid = value && value !== ''

                if (!valid) {
                    setUserRegistrationDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Username is required.'
                            }
                        };
                    });
                }
                else {
                    setUserRegistrationDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else if (field === 'postcode') { // Update user registration postcode
                const valid = value && value !== '' && /^\d+$/.test(value);

                if (!valid) {
                    setUserRegistrationDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Postcode is required and must be numeric.'
                            }
                        };
                    });
                }
                else {
                    setUserRegistrationDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else if (field === 'state') { // Update user registration state
                const valid = value && value !== ''

                if (!valid) {
                    setUserRegistrationDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'State is required.'
                            }
                        };
                    });
                }
                else {
                    setUserRegistrationDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else if (field === 'email') { // Update user registration email
                const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/

                const valid = regex.test(value);
                const errorMessage = !value || value === '' ?
                    'Email is required.' :
                    'Invalid email.'

                if (!valid) {
                    if (warn) {
                        setUserRegistrationDetailsState(c => {
                            return {
                                ...c,
                                [field]: {
                                    state: 'warning',
                                    message: 'Invalid email'
                                }
                            };
                        });
                    }
                    else {
                        setUserRegistrationDetailsState(c => {
                            return {
                                ...c,
                                [field]: {
                                    state: 'error',
                                    message: errorMessage
                                }
                            };
                        });
                    }
                }
                else {
                    setUserRegistrationDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else { // Otherwise, clear all errors/warnings
                setUserRegistrationDetailsState({});
            }

            return true;
        }

        return false;

    }, [setUserRegistrationDetailsState])

    const handleRegistrationChange = useCallback((field: string, value: any) => {
        validateUserRegistrationField(field, value, true);

        setUserRegistrationDetails(c => {
            return new UserRegistration({
                ...c,
                [field]: value
            });
        })
    }, [validateUserRegistrationField, setUserRegistrationDetails]);

    const validatePassword = useCallback(() => {
        const passwordRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]).{8,}$/;

        if (!passwordRegex.test(password.password)) {
            setPasswordState({
                ['password']: {
                    message: 'Password strength does not meet requirements. Password must have a minimum of 8 characters, including at least 1 uppercase character, 1 lowercase character and one special character: !"#$%&\'()*+,-./:;<=>?@[\\\]^_`{|}~.',
                    state: 'error'
                },
                ['confirmPassword']: {
                    message: undefined,
                    state: 'error'
                }
            });

            return false;
        }
        if (password.password !== password.confirmPassword) {
            setPasswordState({
                ['password']: {
                    message: undefined,
                    state: 'error'
                },
                ['confirmPassword']: {
                    message: 'Password fields must be the same.',
                    state: 'error'
                }
            });

            return false;
        }
        else if (password.password === '' || password.confirmPassword === '') {
            let state = {};
            if (password.password === '') {
                state = {
                    ['password']: {
                        message: 'Password is required.',
                        state: 'error'
                    }
                };
            }
            if (password.confirmPassword === '') {
                state = {
                    ...state,
                    ['confirmPassword']: {
                        message: 'Password confirmation is required.',
                        state: 'error'
                    }
                }
            }
            setPasswordState(state);
        }
        else {
            setPasswordState({});
        }

        return true;
    }, [password, setPasswordState])

    const handlePasswordChange = useCallback((field: string, value: string) => {
        setPassword(c => {
            return {
                ...c,
                [field]: value
            };
        })
    }, [setPassword]);

    const handleRegistrationSubmit = useCallback(async () => {

        if (!termsOfUseState.agree) {
            addAlert({
                content: <AlertTitle>
                    {"You must read and agree to the terms of use"}
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
            return;
        }

        if (
            validateUserRegistrationField('fullName', userRegistrationDetails?.fullName) &&
            validateUserRegistrationField('userName', userRegistrationDetails?.userName) &&
            validateUserRegistrationField('email', userRegistrationDetails?.email) &&
            validateUserRegistrationField('postcode', userRegistrationDetails?.postcode) &&
            validateUserRegistrationField('state', userRegistrationDetails?.state) &&
            validatePassword() &&
            termsOfUseState.agree
        ) {
            try {
                // Make registration request
                await client.post(
                    new UserRegistration({
                        fullName: userRegistrationDetails?.fullName,
                        email: userRegistrationDetails?.email,
                        userName: userRegistrationDetails?.userName,
                        postcode: userRegistrationDetails?.postcode,
                        mobileNumber: userRegistrationDetails?.mobileNumber,
                        password: password.password,
                        state: userRegistrationDetails?.state,
                        beNotified: userRegistrationDetails?.beNotified,
                        species: speciesName?.replace("scan", ""),
                        platformType: 0
                    })
                );

                // Refresh the auth user
                await refresh();

                // Redirect to the profile page
                navigate("/" + speciesName + "/profile");

                // Alert user of success
                addAlert({
                    content: <AlertTitle>
                        User registration successful.
                    </AlertTitle>,
                    dismissable: true,
                    severity: 'success',
                    timeout: SUCCESS_TIMEOUT
                });


            } catch (error: any) {
                if (error?.responseStatus) {
                    if (error.responseStatus.errorCode === UserFormatError.errorCode) {
                        let newState: Record<string, FieldState> = {};

                        for (var key of Object.keys(error.responseStatus.meta)) {
                            newState[key] = {
                                message: error.responseStatus.meta[key],
                                state: 'error'
                            }
                        }

                        addAlert({
                            content: <AlertTitle>
                                {newState[Object.keys(newState)[0]].message}
                            </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });

                        setUserRegistrationDetailsState(newState);
                    }
                    else {
                        addAlert({
                            content: <AlertTitle>
                                {error.responseStatus.message}
                            </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });
                    }
                }
                else {
                    addAlert({
                        content: <AlertTitle>
                            {error.message}
                        </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });
                }
            }
        }

    }, [client, userRegistrationDetails, validateUserRegistrationField, validatePassword, addAlert, setUserRegistrationDetailsState, termsOfUseState]);

    return (
        <RegisterContainer>
            <Typography color="#333" variant='h2'>Create a new account</Typography>
            <Hr color={grey[400]} sx={{ margin: '40px 0' }} />

            <Table>
                <TableBody>
                    <StyledTableRow
                        primaryLabel="Name"
                        fieldState={userRegistrationDetailsState['fullName']}
                        value={userRegistrationDetails?.fullName}
                        onChange={(v) => handleRegistrationChange('fullName', v.target.value)}
                    />
                    <StyledTableRow
                        primaryLabel="Username"
                        fieldState={userRegistrationDetailsState['userName']}
                        value={userRegistrationDetails?.userName}
                        onChange={(v) => handleRegistrationChange('userName', v.target.value)}
                    />
                    <StyledTableRow
                        primaryLabel="Email"
                        fieldState={userRegistrationDetailsState['email']}
                        value={userRegistrationDetails?.email}
                        onChange={(v) => handleRegistrationChange('email', v.target.value)}
                    />
                    <StyledTableRow
                        primaryLabel="Mobile"
                        fieldState={userRegistrationDetailsState['mobile']}
                        secondaryLabel="Optional"
                        value={userRegistrationDetails?.mobileNumber}
                        onChange={(v) => handleRegistrationChange('mobile', v.target.value)}
                    />
                    <StyledTableRow
                        type="password"
                        primaryLabel="Password"
                        fieldState={passwordState['password']}
                        value={password.password}
                        onChange={(v) => handlePasswordChange('password', v.target.value)}
                    />
                    <StyledTableRow
                        type="password"
                        primaryLabel="Confirm Password"
                        fieldState={passwordState['confirmPassword']}
                        value={password.confirmPassword}
                        onChange={(v) => handlePasswordChange('confirmPassword', v.target.value)}
                    />
                    <StyledTableRow
                        primaryLabel="Postcode"
                        secondaryLabel="Numbers Only"
                        fieldState={userRegistrationDetailsState['postcode']}
                        value={userRegistrationDetails?.postcode}
                        onChange={(v) => handleRegistrationChange('postcode', v.target.value)}
                    />
                    <StyledTableRow
                        select
                        items={states}
                        primaryLabel="State"
                        fieldState={userRegistrationDetailsState['state']}
                        value={userRegistrationDetails?.state}
                        onChange={(v) => handleRegistrationChange('state', v.target.value)}
                    />
                </TableBody>
            </Table>

            {
                termsOfUseContentLoading ?
                    <Center>
                        <CircularProgress
                            sx={{
                                color: theme.palette.primary.main,
                                marginTop: '40px',
                                maxHeight: '40px',
                                maxWidth: '40px'
                            }}
                        />
                    </Center> :
                    termsOfUseState?.contentVersion?.text &&
                    <StyledTermsOfUseParentContainer>
                        <Typography fontWeight={'600'}>Website terms of use and privacy:</Typography>
                        <StyledTermsOfUseContainer>
                            { parse(DOMPurify.sanitize(termsOfUseState.contentVersion.text)) }
                        </StyledTermsOfUseContainer>
                        <div>
                            <Checkbox
                                checked={termsOfUseState.agree}
                                onChange={() => setTermsOfUseState({ agree: !termsOfUseState.agree, contentVersion: termsOfUseState.contentVersion })}
                            />
                            <Typography display='inline'>I have read and agree to the terms of use</Typography>
                        </div>
                        <div>
                            <Checkbox checked={!!userRegistrationDetails?.beNotified} onChange={(v) => handleRegistrationChange('beNotified', v.target.checked)} />
                            <Typography display='inline'>I wish to be notified about updates to FeralScan</Typography>
                        </div>
                    </StyledTermsOfUseParentContainer>
            }

            <Center ha='end' m='15px 10px'>
                <StyledConfirmButton
                    disabled={termsOfUseContentLoading}
                    onClick={handleRegistrationSubmit}
                >
                    <Typography fontWeight='600'>Submit</Typography>
                </StyledConfirmButton>
            </Center>

        </RegisterContainer>
    )
}

const RegisterPage: FC = () => {
    const { user } = useContext(AuthContext);
    const { species: speciesName } = useParams();

    if (user) {
        return <Navigate to={"/" + speciesName + "/profile"} replace />
    }
    return (<RegisterContent />)
}
export default RegisterPage
